import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, BookOpen, BookOpenCheck, Settings, Moon, Sun, Users, Brain, Repeat, ShieldAlert, BookUser, ScrollText, GraduationCap, Plane, LogOut, Shield } from 'lucide-react';
import { useWorkPassCapabilities } from '../hooks/useWorkPassDecisions';
import { useTerminationCapabilities } from '../hooks/useTerminationRequests';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { CountBadge } from '../components/UI/CountBadge';
import { useAuth } from '../contexts/AuthContext';
import { usePassportReturnSummary } from '../hooks/usePassportReturns';
import {
  countOpenEmployeeOffboardingTasks,
  useMyOffboardingCase,
} from '../hooks/useOffboardingCases';

// Icon colour is driven by the *section* a card belongs to, not picked
// per-card. Each section owns one semantic colour so the hue becomes a
// learnable cue (e.g. amber == "report a concern") rather than decoration.
const colorClasses = {
  blue:   { bg: 'bg-blue-100',   text: 'text-blue-800' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  teal:   { bg: 'bg-teal-100',   text: 'text-teal-700' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-700' },
  amber:  { bg: 'bg-amber-100',  text: 'text-amber-700' },
};

const Card = ({ to, icon: Icon, title, desc, color = 'blue', pendingCount = 0 }) => {
  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <Link
      to={to}
      className="card hover:shadow-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    >
      <div className="card-content">
        <div className="flex items-center gap-4 mb-3">
          <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-subsection-header flex items-center gap-2">
              {title}
              {pendingCount > 0 && (
                <CountBadge count={pendingCount} theme="warning" />
              )}
            </h3>
          </div>
        </div>
        <p className="text-body">{desc}</p>
      </div>
    </Link>
  );
};

// Section grouping keeps every service one tap away (no nesting / hidden
// sub-menus) while giving the eye structure and letting colour carry meaning.
const buildSections = (
  showTradeTransfer,
  showPassportReturn,
  pendingPassportActionCount,
  showMyOffboarding,
  pendingOffboardingCount,
  showWorkPass,
  pendingWorkPassCount,
  showTermination,
  showResignationNotice,
  pendingResignationCount,
) => [
  {
    title: 'Requests & Approvals',
    color: 'blue',
    items: [
      { to: '/self-service/letters', icon: Mail, title: 'Request Letters', desc: 'Request company letters and track your requests' },
      { to: '/self-service/nightwork', icon: Moon, title: 'Nightwork Requests', desc: 'Submit nightwork requests and track approval status' },
      { to: '/self-service/rest-day-work', icon: Sun, title: 'Rest Day & PH Work Request', desc: 'Request approval for work on rest days, Sundays, or public holidays' },
      { to: '/self-service/course-requests', icon: GraduationCap, title: 'Course Requests', desc: 'Request training for workers or yourself and track HOD, HR, and booking status' },
      ...(showTradeTransfer
        ? [{ to: '/self-service/trade-transfer', icon: Repeat, title: 'Worker Trade Transfer', desc: 'Request to move workers between trades with acknowledgement from the worker, current Trade Leader, and HOD' }]
        : []),
      ...(showPassportReturn
        ? [{
            to: '/self-service/passport-return',
            icon: BookUser,
            title: 'Passport Return',
            desc: 'Return passports previously held by HR and acknowledge receipt',
            pendingCount: pendingPassportActionCount,
          }]
        : []),
      {
        to: '/self-service/travel-notice',
        icon: Plane,
        title: 'Overseas Travel Notice',
        desc: 'Notify HR before overseas travel on a rest day or day off — no approval needed',
      },
      ...(showMyOffboarding
        ? [{
            to: '/self-service/offboarding',
            icon: LogOut,
            title: 'My Offboarding',
            desc: 'View your last day and propose a notice offset if it still applies',
            pendingCount: pendingOffboardingCount,
          }]
        : []),
      ...(showWorkPass
        ? [{
            to: '/self-service/work-pass-decisions',
            icon: Shield,
            title: 'Work Pass Decisions',
            desc: 'Recommend retain or end for workers entering the 90-day expiry window',
            pendingCount: pendingWorkPassCount,
          }]
        : []),
      ...(showTermination
        ? [{
            to: '/self-service/termination-requests',
            icon: LogOut,
            title: 'Termination Requests',
            desc: 'Request a worker termination or record that a worker chose to leave',
          }]
        : []),
      ...(showResignationNotice
        ? [{
            to: '/self-service/resignation-notice',
            icon: ScrollText,
            title: 'Review resignation notice',
            desc: 'Confirm the dates and that you chose to leave — no drawn signature',
            pendingCount: pendingResignationCount,
          }]
        : []),
    ],
  },
  {
    title: 'Resources',
    color: 'indigo',
    items: [
      { to: '/self-service/handbook', icon: BookOpen, title: 'Employee Handbook', desc: 'Read the full company handbook, policies and benefits' },
      { to: '/self-service/policies', icon: ScrollText, title: 'Company Policies', desc: 'Read and acknowledge our Code of Conduct and governance policies' },
      { to: '/self-service/training-materials', icon: BookOpenCheck, title: 'Training Materials', desc: 'Read company training and awareness resources at your own pace' },
    ],
  },
  {
    title: 'Events',
    color: 'teal',
    items: [
      { to: '/self-service/events', icon: Users, title: 'Meetings & Training Attendance', desc: 'Host an event and capture attendance with a rotating QR code' },
    ],
  },
  {
    title: 'Personal Insights',
    hint: 'Voluntary & confidential',
    color: 'violet',
    items: [
      { to: '/self-service/big-five', icon: Brain, title: 'Big Five Profile', desc: 'Take the Big Five personality assessment and explore your results' },
    ],
  },
  {
    title: 'Report a Concern',
    color: 'amber',
    items: [
      { to: '/self-service/whistleblowing', icon: ShieldAlert, title: 'Anonymous Misconduct Reporting', desc: 'Report misconduct, fraud, or compliance breaches confidentially — your identity is never collected or stored' },
    ],
  },
];

const SelfService = () => {
  const { user } = useAuth();
  const showTradeTransfer = !!user?.can_request_trade_transfer || !!user?.can_oversee_trade_transfer;
  const { data: passportSummary } = usePassportReturnSummary({
    enabled: !!user,
  });
  const showPassportReturn = !!passportSummary?.visible;
  const pendingPassportActionCount = passportSummary?.pending_my_action || 0;
  const { data: myOffboardingCase } = useMyOffboardingCase({
    enabled: !!user,
  });
  const showMyOffboarding = !!myOffboardingCase;
  const pendingOffboardingCount = countOpenEmployeeOffboardingTasks(myOffboardingCase);
  const showWorkPass = !!user?.can_act_work_pass_decision;
  const { data: workPassCaps } = useWorkPassCapabilities({ enabled: showWorkPass });
  const { data: terminationCaps } = useTerminationCapabilities({ enabled: !!user });
  const showTermination = !!user?.can_submit_termination_request;
  const pendingResignationCount = terminationCaps?.pending_resignation_ack || 0;
  const showResignationNotice = pendingResignationCount > 0 || !!terminationCaps?.has_pending_resignation_ack;
  const sections = buildSections(
    showTradeTransfer,
    showPassportReturn,
    pendingPassportActionCount,
    showMyOffboarding,
    pendingOffboardingCount,
    showWorkPass,
    workPassCaps?.pending_work_pass_actions || 0,
    showTermination,
    showResignationNotice,
    pendingResignationCount,
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Page Header — Compact Page Header (employee) */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Settings className="w-5 h-5 text-blue-800" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Self-Service</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">Access company resources and request services</p>
        </div>

        {/* Service Cards — grouped into labelled sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.title} className="space-y-4">
              <div className="flex items-baseline gap-3 px-1">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {section.title}
                </h2>
                {section.hint && (
                  <span className="text-xs text-gray-400">{section.hint}</span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.items.map((item) => (
                  <Card key={item.to} {...item} color={section.color} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SelfService;
